import _ from "lodash";
import client from "lib/apolloClient/client";
import { generateRandomId } from "Utils";
import {
  writeSinglePointRubricElementInCache,
  writeOrganizationSinglePointRubricsInCache,
  getOrganizationSinglePointRubricsFromCache,
} from "./SinglePointRubricGraphqlHelpers";
import { getOrganizationSinglePointRubricsQuery } from "./SinglePointRubricQuery";
import {
  updateSinglePointRubricCriteriaMutation,
  updateSinglePointRubricStandardMutation,
  createSinglePointRubricStandardMutation,
  deleteSinglePointRubricStandardMutation,
  createSinglePointRubricMutation,
  deleteSinglePointRubricMutation,
  bulkCreateSinglePointRubricStandardMutation,
} from "./SinglePointRubricMutation";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  getAssessmentToolNodeDetailsFromCacheV2,
  writeAssessmentToolNodeDetailsInCache,
  getOrganizationConstantsFromCache,
  getStaffBasicDetailsFromCache,
} from "modules/CommonGraphqlHelpers";
import { writeAssessmentToolOfParentInCache } from "modules/AssessmentTool/AssessmentToolGraphqlHelpers";
import { getUpdatedAssessmentToolDetails } from "modules/AssessmentTool/AssessmentToolModule";
import {
  getSelectedAcademicYearId,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import { insertElementInAssessmentTool } from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/modules/utils";

export const saveSinglePointRubric = ({
  copyToolId,
  mode = "CREATE",
  isGlobal = true,
  title = "",
  // assessmentId = null,
  parentId,
  parentType,
  filters,
  curriculumProgramId,
  extraParams = {},
  portalType,
  shouldRefetchQuery = false,
} = {}) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo({ portalType });
    const state = getState();
    const rubricId = copyToolId;
    const organizationId = userInfo.org_id;
    const academicYearId = getSelectedAcademicYearId(state);

    const organizationDeatils = getOrganizationSinglePointRubricsFromCache({
      id: organizationId,
      filters,
    });

    const singlePointRubrics =
      _.get(organizationDeatils, "singlePointRubrics", []) || [];
    const organizationConstants = getOrganizationConstantsFromCache(
      organizationId
    );

    const singlePointRubricCriterias =
      _.get(extraParams, "criterias") ||
      _.get(organizationConstants, "singlePointRubricCriterias", []);

    const criterias = _.map(singlePointRubricCriterias, criteria => {
      return {
        ...criteria,
        isCriteriaStandardColumn: !!criteria.isCriteriaStandardColumn,
        id: generateRandomId(),
        __typename: "SinglePointRubricCriteria",
      };
    });

    const standards = [];

    if (_.has(extraParams, "standards")) {
      const _standards = _.map(extraParams.standards, standard => {
        return {
          ...standard,
          id: generateRandomId(),
          __typename: "SinglePointRubricStandard",
        };
      });

      standards.push(..._standards);
    } else {
      standards.push({
        id: generateRandomId(),
        title: "",
        subTitle: "",
        __typename: "SinglePointRubricStandard",
      });
    }

    let rubricData = getAssessmentToolNodeDetailsFromCacheV2({
      id: rubricId,
      type: "SINGLE_POINT_RUBRIC",
    });

    if (mode == "CREATE") {
      rubricData = {
        id: generateRandomId(),
        title,
        standards,
        criterias,
        createdBy: getStaffBasicDetailsFromCache(userInfo.id),
        __typename: "SinglePointRubric",
      };
    } else {
      rubricData = {
        ...rubricData,
        id: generateRandomId(),
        title,
        createdBy: getStaffBasicDetailsFromCache(userInfo.id),
      };
    }

    // If true, default criterias will be used in BE. No need to send from FE (except for AI)
    const useDefaultCriteria = _.isEmpty(_.get(extraParams, "criterias", []));

    const refetchQueries = [];
    if (shouldRefetchQuery) {
      refetchQueries.push({
        query: getOrganizationSinglePointRubricsQuery,
        variables: {
          id: organizationId,
          filters,
        },
      });
    }

    const variables = {
      createdBy: userInfo.id,
      criterias: useDefaultCriteria
        ? undefined
        : _.map(criterias, criteria =>
            _.pick(criteria, ["title", "subTitle", "isCriteriaStandardColumn"])
          ),
      standards: _.map(standards, standard =>
        _.pick(standard, ["title", "subTitle"])
      ),
      mode,
      title,
      rubricId,
      isGlobal,
      parentId,
      parentType,
      curriculumProgramId:
        isGlobal || useDefaultCriteria
          ? curriculumProgramId || getSelectedCurriculumId(getState())
          : undefined,
      portalType,
    };

    try {
      const result = await client.mutate({
        mutation: createSinglePointRubricMutation,
        variables: variables,
        awaitRefetchQueries: shouldRefetchQuery,
        refetchQueries,
        // optimisticResponse: {
        //   __typename: "Mutation",
        //   planner: {
        //     __typename: "PlannerMutations",
        //     createSinglePointRubric: rubricData
        //   }
        // },
        update: (
          cache,
          {
            data: {
              planner: {
                createSinglePointRubric: createSinglePointRubricMutationResponse,
              },
            },
          }
        ) => {
          if (!_.isEmpty(createSinglePointRubricMutationResponse)) {
            if (isGlobal && !shouldRefetchQuery) {
              // isGlobal is true when you save tool as template
              const data = {
                ...organizationDeatils,
                singlePointRubrics: [
                  ...singlePointRubrics,
                  createSinglePointRubricMutationResponse,
                ],
              };

              setTimeout(() =>
                writeOrganizationSinglePointRubricsInCache({
                  id: organizationId,
                  data,
                  filters,
                })
              );
            }

            if (parentId) {
              const assessmentToolDetails = dispatch(
                getUpdatedAssessmentToolDetails({
                  parentId,
                  parentType,
                  mutationResponse: createSinglePointRubricMutationResponse,
                  assessmentToolType: "SINGLE_POINT_RUBRIC",
                  extraParams,
                })
              );

              setTimeout(() =>
                writeAssessmentToolOfParentInCache({
                  id: parentId,
                  data: assessmentToolDetails,
                  type: parentType,
                  academicYearId,
                })
              );
            }
          }
        },
      });

      // show toast only when user save tool as template
      if (mode === "COPY" && isGlobal) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:label_successfully_duplicated",
            locale_params: [
              {
                key: "label",
                value: "common:single_point_rubric",
                isPlainText: false,
              },
            ],
            type: "success",
          })
        );
      }
      return _.get(result, "data.planner.createSinglePointRubric.id", null);
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      setTimeout(() =>
        writeOrganizationSinglePointRubricsInCache({
          id: organizationId,
          data: organizationDeatils,
          filters,
        })
      );
      console.error(e);
      return null;
    }
  };
};

export const deleteSinglePointRubric = ({
  id,
  filters,
  showDiscardToast = false,
}) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo();
    const organizationId = userInfo.org_id;

    const organizationDeatils = getOrganizationSinglePointRubricsFromCache({
      id: organizationId,
      filters,
    });
    const singlePointRubrics = _.get(
      organizationDeatils,
      "singlePointRubrics",
      []
    );
    try {
      await client.mutate({
        mutation: deleteSinglePointRubricMutation,
        variables: {
          id: id,
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            __typename: "PlannerMutations",
            deleteSinglePointRubric: {
              isSuccess: true,
              warning: null,
              __typename: "DeleteOutput",
            },
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { deleteSinglePointRubric },
            },
          }
        ) => {
          if (deleteSinglePointRubric.isSuccess) {
            const updatedRubrics = _.filter(
              singlePointRubrics,
              rub => rub.id != id
            );
            const data = {
              ...organizationDeatils,
              singlePointRubrics: updatedRubrics,
            };

            setTimeout(() =>
              writeOrganizationSinglePointRubricsInCache({
                id: organizationId,
                data,
                filters,
              })
            );
          }
        },
      });
      dispatch(
        setToastMsg({
          msg: showDiscardToast
            ? "toastMsgs:label_successfully_discarded"
            : "toastMsgs:label_successfully_deleted",
          type: "success",
          locale_params: [
            {
              key: "label",
              value: "common:single_point_rubric",
              isPlainText: false,
            },
          ],
        })
      );
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      setTimeout(() =>
        writeOrganizationSinglePointRubricsInCache({
          id: organizationId,
          data: organizationDeatils,
          filters,
        })
      );
      console.error(e);
    }
  };
};

export const createSinglePointRubricStandard = ({
  rubricId,
  portalType,
  putAfterItemId,
}) => {
  return async (dispatch, getState) => {
    const rubricData = getAssessmentToolNodeDetailsFromCacheV2({
      id: rubricId,
      type: "SINGLE_POINT_RUBRIC",
    });
    const userInfo = getUserInfo({ portalType });
    try {
      const standards = _.get(rubricData, "standards", []);
      if (_.isUndefined(putAfterItemId)) {
        // to handle old flow where putAfterItemId does not exist
        putAfterItemId = _.get(_.last(standards), "id", null);
      }
      const putAfterItemIndex = _.findIndex(standards, {
        id: putAfterItemId,
      });
      const newStandard = {
        __typename: "SinglePointRubricStandard",
        id: generateRandomId(),
        title: "",
        subTitle: "",
        displaySequence: putAfterItemIndex + 1,
      };

      await client.mutate({
        mutation: createSinglePointRubricStandardMutation,
        variables: {
          ..._.omit(newStandard, ["id", "__typename"]),
          createdBy: userInfo.id,
          rubricId,
          portalType,
          putAfterItemId,
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            __typename: "PlannerMutations",
            createSinglePointRubricStandard: newStandard,
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { createSinglePointRubricStandard },
            },
          }
        ) => {
          let standards = _.get(rubricData, "standards", []);
          standards = insertElementInAssessmentTool({
            putAfterItemId,
            assessmentTool: standards,
            elementToAdd: createSinglePointRubricStandard,
          });

          const data = {
            ...rubricData,
            standards,
          };

          setTimeout(() =>
            writeAssessmentToolNodeDetailsInCache({
              id: rubricId,
              data,
              type: "SINGLE_POINT_RUBRIC",
            })
          );
        },
      });
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      setTimeout(() =>
        writeAssessmentToolNodeDetailsInCache({
          id: rubricId,
          data: rubricData,
          type: "SINGLE_POINT_RUBRIC",
        })
      );
    }
  };
};

export const deleteSinglePointRubricStandard = ({
  id,
  rubricId,
  portalType,
}) => {
  return async (dispatch, getState) => {
    let rubricData = getAssessmentToolNodeDetailsFromCacheV2({
      id: rubricId,
      type: "SINGLE_POINT_RUBRIC",
    });
    try {
      await client.mutate({
        mutation: deleteSinglePointRubricStandardMutation,
        variables: {
          id: id,
          portalType,
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            __typename: "PlannerMutations",
            deleteSinglePointRubricStandard: {
              isSuccess: true,
              warning: null,
              __typename: "DeleteOutput",
            },
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { deleteSinglePointRubricStandard },
            },
          }
        ) => {
          if (deleteSinglePointRubricStandard.isSuccess) {
            let standards = _.get(rubricData, "standards", []);

            standards = _.filter(standards, obj => obj.id != id);

            rubricData = {
              ...rubricData,
              standards,
            };
            setTimeout(() =>
              writeAssessmentToolNodeDetailsInCache({
                id: rubricId,
                data: rubricData,
                type: "SINGLE_POINT_RUBRIC",
              })
            );
          }
        },
      });
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      setTimeout(() =>
        writeAssessmentToolNodeDetailsInCache({
          id: rubricId,
          data: rubricData,
          type: "SINGLE_POINT_RUBRIC",
        })
      );
    }
  };
};

export const updateSinglePointRubricElement = ({
  id,
  type,
  params,
  portalType,
}) => {
  return async (dispatch, getState) => {
    try {
      let mutation = "";
      switch (type) {
        case "STANDARD":
          mutation = updateSinglePointRubricStandardMutation;
          break;
        case "CRITERIA":
          mutation = updateSinglePointRubricCriteriaMutation;
          break;
      }

      await client.mutate({
        mutation,
        variables: {
          ...params,
          id,
          portalType,
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
};

export const writeSinglePointRubricElementLocal = ({
  id,
  type,
  params,
  elementDetails,
}) => {
  return (dispatch, getState) => {
    const updatedElementDetails = { ...elementDetails, ...params };

    writeSinglePointRubricElementInCache({
      id,
      type,
      data: updatedElementDetails,
    });
  };
};

export const bulkCreateSinglePointRubricStandard = params => {
  return async (dispatch, getState) => {
    const { input } = params;

    try {
      await client.mutate({
        mutation: bulkCreateSinglePointRubricStandardMutation,
        variables: {
          input,
        },
        update: (
          cache,
          {
            data: {
              planner: { bulkCreateSinglePointRubricStandard },
            },
          }
        ) => {
          const rubricId = _.get(_.first(input), "rubricId");

          const response = _.get(
            bulkCreateSinglePointRubricStandard,
            "response",
            []
          );

          if (!_.isEmpty(response)) {
            const rubric = getAssessmentToolNodeDetailsFromCacheV2({
              id: rubricId,
              type: "SINGLE_POINT_RUBRIC",
            });

            const standards = _.get(rubric, "standards", []);

            const updatedStandards = [...standards, ...response];

            const updatedSinglePointRubric = {
              ...rubric,
              standards: updatedStandards,
            };

            writeAssessmentToolNodeDetailsInCache({
              id: rubricId,
              data: updatedSinglePointRubric,
              type: "SINGLE_POINT_RUBRIC",
            });
          }
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
};

const REDUCER_HANDLERS = {};

const initialState = {};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
